import { useEffect, useState } from 'react';
import cn from 'classnames';

import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { Button } from 'UIPackage/MainModule/components/Button';
import {
  IKnmErknmFormViolatedRequirementsMandatory,
  IKnmErknmFormViolatedRequirementsNpa,
} from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';

import styles from './SelectViolationModal.module.scss';
import { ViolationItem } from './ViolationItem';

interface ISelectViolationModalProps {
  requirementsNpa: IKnmErknmFormViolatedRequirementsNpa[] | undefined;
  visible: boolean;
  onClose: () => void;
  onSubmit: (items: IKnmErknmFormViolatedRequirementsNpa[]) => void;
}

export const SelectViolationModal = ({ requirementsNpa, visible, onClose, onSubmit }: ISelectViolationModalProps) => {
  const [selectedRequirements, setSelectedRequirements] = useState<IKnmErknmFormViolatedRequirementsNpa[]>([]);

  useEffect(() => {
    if (visible) setSelectedRequirements([]);
  }, [visible]);

  const isSelected = (
    requirementNpa: IKnmErknmFormViolatedRequirementsNpa,
    mandatory: IKnmErknmFormViolatedRequirementsMandatory,
    violation: IKnmErknmFormViolation,
  ): boolean => {
    return !!selectedRequirements.find(item => {
      if (item.guid === requirementNpa.guid) {
        return item.violatedMandatory?.find(m => {
          if (m.mandatory.id === mandatory.mandatory.id) {
            return m.violations?.find(v => v.id === violation.id);
          }

          return false;
        });
      }

      return false;
    });
  };

  const handleSelect = (
    requirementNpa: IKnmErknmFormViolatedRequirementsNpa,
    mandatory: IKnmErknmFormViolatedRequirementsMandatory,
    violation: IKnmErknmFormViolation,
  ) => {
    setSelectedRequirements(prev => {
      const npaIndex = prev.findIndex(item => item.guid === requirementNpa.guid);

      if (npaIndex === -1) {
        // Создаем новый NPA с нарушением
        return [
          ...prev,
          {
            ...requirementNpa,
            violatedMandatory: [{ ...mandatory, violations: [violation] }],
          },
        ];
      }

      // Работаем с существующим NPA
      return prev
        .map((npa, idx) => {
          if (idx !== npaIndex) return npa;

          const mandatoryIndex = npa.violatedMandatory?.findIndex(m => m.mandatory.id === mandatory.mandatory.id) ?? -1;

          if (mandatoryIndex === -1) {
            // Добавляем новый mandatory requirement с нарушением
            return {
              ...npa,
              violatedMandatory: [...(npa.violatedMandatory || []), { ...mandatory, violations: [violation] }],
            };
          }

          // Обновляем существующий mandatory requirement
          const updatedMandatory = npa.violatedMandatory
            .map((m, mIdx) => {
              if (mIdx !== mandatoryIndex) return m;

              const hasViolation = m.violations?.some(v => v.id === violation.id);

              if (hasViolation) {
                // Удаляем нарушение
                return {
                  ...m,
                  violations: m.violations?.filter(v => v.id !== violation.id) || [],
                };
              }

              // Добавляем нарушение
              return {
                ...m,
                violations: [...(m.violations || []), violation],
              };
            })
            .filter(m => m.violations?.length);

          return {
            ...npa,
            violatedMandatory: updatedMandatory,
          };
        })
        .filter(npa => npa.violatedMandatory?.length);
    });
  };

  const handleSubmit = () => {
    const selectedRequirementsWithNotFixedViolations = selectedRequirements.map(requirement => ({
      ...requirement,
      violatedMandatory: requirement.violatedMandatory.map(mandatory => ({
        ...mandatory,
        violations:
          mandatory.violations
            ?.filter(
              violation =>
                violation.status === ViolationStatusesEnum.NOT_FIXED ||
                violation.status === ViolationStatusesEnum.APPROVING,
            )
            .map(violation => ({ ...violation, isNew: true })) || null,
      })),
      isNew: true,
    }));

    onSubmit(selectedRequirementsWithNotFixedViolations);
    onClose();
  };

  return (
    <Modal visible={visible} onClose={onClose}>
      <ModalHeader title="Добавление выявленного нарушения обязательных требований" sizeText="medium" />
      <ModalBody>
        {requirementsNpa?.length && (
          <div className={styles.Table}>
            <div className={styles.Row}>
              <div className={cn(styles.Cell, styles.CellHeader, styles.PaddingLeftHeaderCell, styles.CellWidthStatus)}>
                СТАТУС
              </div>
              <div className={cn(styles.Cell, styles.CellHeader, styles.CellWidthDescription)}>ОПИСАНИЕ НАРУШЕНИЯ</div>
            </div>
            <div className={styles.Body}>
              {requirementsNpa?.map(requirementNpa => {
                if (!requirementNpa.violatedMandatory || !requirementNpa.violatedMandatory?.length) return null;

                return requirementNpa.violatedMandatory?.map(mandatory => {
                  if (!mandatory.violations) return null;

                  return mandatory.violations.map(violation => {
                    return (
                      <ViolationItem
                        key={violation.id}
                        handleSelect={() => handleSelect(requirementNpa, mandatory, violation)}
                        violation={violation}
                        isSelected={isSelected(requirementNpa, mandatory, violation)}
                        requirementNpa={requirementNpa}
                        mandatory={mandatory}
                      />
                    );
                  });
                });
              })}
            </div>
          </div>
        )}
        {!requirementsNpa?.length && (
          <div className={styles.EmptyModalText}>
            Для отображения записей выявленных нарушенных обязательных требований необходимо заполнить их в записи
            раздела «Сведения о решении» требуемого контролируемого лица
          </div>
        )}
      </ModalBody>
      <ModalActions>
        <Button
          text="Добавить"
          color="primary"
          size="medium"
          onClick={handleSubmit}
          disabled={!selectedRequirements.length}
        />
        <Button text="Отмена" size="medium" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};
